/**
 * errorTransform.ts
 *
 * Converts ConnectError into a plain Error carrying a Vietnamese message
 * safe to show the user.
 */
import { type Code, ConnectError, type Interceptor } from "@connectrpc/connect";
import { sanitizeErrorMessage } from "@/services/errorMessages";

/**
 * The error a backend failure surfaces as once it has been made user-safe.
 *
 * It is deliberately not a ConnectError: callers show `message` as is. It
 * keeps the original Connect `code`, because the Connect transport re-wraps
 * whatever an interceptor throws as a `ConnectError` with `Code.Unknown`
 * (this error becomes its `cause`). Without the code here, error reporting
 * could no longer tell an expired session from a server failure.
 */
export class BackendError extends Error {
  /** The Connect code the backend (or the auth interceptor) responded with. */
  readonly code: Code;

  constructor(message: string, code: Code, options?: ErrorOptions) {
    super(message, options);
    this.name = "BackendError";
    this.code = code;
  }
}

/**
 * Builds the error-transformation interceptor.
 *
 * A ConnectError is replaced by a {@link BackendError} whose message has been
 * passed through {@link sanitizeErrorMessage} and which keeps the original
 * code. Anything that is not a ConnectError is rethrown untouched.
 *
 * @returns A Connect interceptor.
 */
export function createErrorTransformInterceptor(): Interceptor {
  return (next) => async (req) => {
    try {
      return await next(req);
    } catch (error) {
      if (error instanceof ConnectError) {
        // Get raw message from error
        const rawMessage = error.rawMessage || error.message || "";

        // Sanitize and map to Vietnamese message
        const errorMessage = sanitizeErrorMessage(rawMessage, error.code);

        // Keep the original for Sentry's linked errors; users only see `message`.
        throw new BackendError(errorMessage, error.code, { cause: error });
      }
      throw error;
    }
  };
}
